import {
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  getTagIconFromLabel,
  PHOTOGRAPHY_TAGS,
  PhotographyTag
} from '../../entities/photography/photography-tag.entity';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';

@Component({
  selector: 'app-tags-selector',
  standalone: true,
  imports: [ReactiveFormsModule, TranslocoPipe],
  templateUrl: './tags-selector.html',
  styleUrl: './tags-selector.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsSelector),
      multi: true
    }
  ]
})
export class TagsSelector implements OnInit, OnDestroy, ControlValueAccessor {
  /** UI state */
  public filteredTags: PhotographyTag[] = PHOTOGRAPHY_TAGS;
  public selectedTags: string[] = [];
  public tagsSuggestionsShown = false;
  public searchCtrl = new FormControl<string>('', { nonNullable: true });

  /** Config */
  @Input() max = 5;
  @Input() position: 'top' | 'bottom' = 'bottom';

  /** Optional outputs for parent UX (toast, etc.) */
  @Output() maxReached = new EventEmitter<void>();

  /** Utilities */
  protected readonly getTagIconFromLabel = getTagIconFromLabel;

  /** Internals */
  private search$ = new Subject<string>();
  private subs: Subscription[] = [];

  /** CVA callbacks */
  private onChange: (val: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    // Stream user input into debounced search
    this.subs.push(
      this.searchCtrl.valueChanges.subscribe(v => this.search$.next(v))
    );
    this.subs.push(
      this.search$.pipe(
        debounceTime(200),
        distinctUntilChanged()
      ).subscribe(q => this.filterTags(q))
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ---- ControlValueAccessor ----
  writeValue(value: string[] | null): void {
    // Reflect initial value from parent form
    this.selectedTags = Array.isArray(value) ? [...value] : [];
  }

  registerOnChange(fn: (val: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.searchCtrl.disable({ emitEvent: false });
    else this.searchCtrl.enable({ emitEvent: false });
  }

  // ---- Filtering + selection logic ----
  private filterTags(query: string): void {
    const q = (query ?? '').trim().toLowerCase();
    this.filteredTags = q
      ? PHOTOGRAPHY_TAGS.filter(t => t.label.toLowerCase().includes(q))
      : PHOTOGRAPHY_TAGS;
    this.tagsSuggestionsShown = true; // keep visible while interacting
  }

  public selectTag(tag: PhotographyTag): void {
    if (this.selectedTags.includes(tag.label)) {
      // Already selected → just clear the search
      this.clearSearch();
      return;
    }

    if (this.selectedTags.length >= this.max) {
      // Don’t add more; notify parent to show a toast if needed
      this.maxReached.emit();
      this.clearSearch();
      return;
    }

    this.selectedTags = [...this.selectedTags, tag.label];
    this.propagate();
    this.clearSearch();
  }

  public removeTag(label: string): void {
    this.selectedTags = this.selectedTags.filter(l => l !== label);
    this.propagate();
  }

  private propagate(): void {
    // Always propagate a new array reference (immutable pattern)
    this.onChange([...this.selectedTags]);
    this.onTouched();
  }

  private clearSearch(): void {
    this.searchCtrl.setValue('', { emitEvent: true });
    this.tagsSuggestionsShown = false;
  }

  // Keyboard: Enter selects first suggestion if any
  public handleEnter(): void {
    if (this.filteredTags.length > 0) {
      this.selectTag(this.filteredTags[0]);
    }
  }

  public handleFocusIn(): void {
    // Show only if we have suggestions or user starts typing
    this.tagsSuggestionsShown = (this.searchCtrl.value?.length ?? 0) > 0 || this.filteredTags.length > 0;
  }

  public handleFocusOut(): void {
    this.tagsSuggestionsShown = false;
  }

  public handleBlur(): void {
    // Do not close immediately; handled by document click to allow mousedown on <li>
    this.onTouched();
  }

  // Close on outside click
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('app-tags-selector')) {
      this.tagsSuggestionsShown = false;
    }
  }
}
